import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CloudUpload, HardDrive, Loader2, Unplug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, errMsg } from "@/lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "mai");

export const DriveBackup = () => {
  const api = useApi();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const load = () => api.get("/drive/status").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    const flag = params.get("drive");
    if (flag) {
      if (flag === "ok") toast.success("Google Drive collegato");
      else toast.error("Collegamento a Google Drive non riuscito");
      params.delete("drive");
      setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!s?.running) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [s]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const connect = () => act(async () => { window.location.href = (await api.post("/drive/connect")).data.url; });
  const backupNow = () => act(async () => { await api.post("/drive/backup-now"); toast.success("Backup avviato"); setTimeout(load, 1500); });
  const disconnect = () => act(async () => { await api.delete("/drive"); toast.success("Google Drive scollegato"); load(); });
  if (!s) return null;
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-3" data-testid="drive-backup-section">
      <div className="flex flex-wrap items-center gap-3">
        <HardDrive className="h-5 w-5 text-emerald-400" />
        <h2 className="text-base md:text-lg font-semibold mr-auto">Backup su Google Drive</h2>
        {s.connected ? (
          <>
            <Button onClick={backupNow} disabled={busy || s.running} data-testid="drive-backup-now-btn">
              {s.running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CloudUpload className="h-4 w-4 mr-1" />}{s.running ? "Backup in corso" : "Fai backup ora"}
            </Button>
            <Button variant="ghost" onClick={disconnect} disabled={busy} data-testid="drive-disconnect-btn"><Unplug className="h-4 w-4 mr-1" />Scollega</Button>
          </>
        ) : (
          <Button onClick={connect} disabled={busy || !s.configured} data-testid="drive-connect-btn">Collega Google Drive</Button>
        )}
      </div>
      <p className="text-sm text-slate-400" data-testid="drive-status-text">
        {!s.configured ? "Mancano le credenziali Google: vanno inserite prima di collegare Drive."
          : s.connected ? <>Collegato{s.email ? ` a ${s.email}` : ""} · cartella “{s.folder}” · ultimo backup: <b className="text-slate-200" data-testid="drive-last-backup">{when(s.last_backup_at)}</b></>
          : "Non collegato. Collega il tuo Google Drive per ricevere il backup ogni notte."}
      </p>
      {s.last_error && <p className="text-xs text-red-300" data-testid="drive-last-error">Ultimo errore: {s.last_error}</p>}
      <p className="text-xs text-slate-500">Ogni notte alle 3:00 la cartella viene aggiornata sovrascrivendo due file: un Excel con preventivi, righe, clienti, catalogo, kit e impostazioni, e uno zip con il PDF di ogni preventivo.</p>
    </section>
  );
};
